/**
 * Whether this device is being touched or pointed at.
 *
 * Read as state rather than written as a CSS media query, for the same reason
 * `useImmersive` is: several of the decisions that hang off it are not layout
 * at all. A key bar for a terminal, an install sheet, and a wake lock are
 * behaviour, and behaviour keyed off `@media (pointer: coarse)` is a decision
 * no test in this repo can reach — Playwright can emulate `hasTouch`, and
 * jsdom can stub `matchMedia`, and neither can assert on a rule inside a media
 * block that never ran.
 *
 * `(pointer: coarse)` rather than `'ontouchstart' in window` or a user-agent
 * test. The question being asked is "is the primary pointer a finger", and that
 * is the query that answers it: a laptop with a touchscreen reports `fine`
 * because its *primary* pointer is a trackpad, which is the right answer — that
 * operator has a keyboard and does not want a key bar taking a row from the
 * terminal. A touch-capability test says yes to that laptop and gets it wrong.
 *
 * Watched rather than read once: a tablet with a keyboard folio attached and
 * detached changes this without a reload, and a console that needed reloading
 * to notice would be a console that lost the key bar exactly when the keyboard
 * went away.
 */

import { useCallback, useSyncExternalStore } from 'react'

const COARSE = '(pointer: coarse)'
/** Narrow enough that two columns are two unreadable columns. The same 900px
 *  the cockpit's stylesheet already stacks its terminals at, so the layout and
 *  the behaviour turn over together rather than at two different widths. */
const NARROW = '(max-width: 900px)'

function list(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia(query)
}

/** Subscribes to one media query as an external store. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      const mql = list(query)
      // `addListener` is the Safari 13 spelling and is still what iOS below 14
      // provides. The optional calls make this inert rather than throwing where
      // neither exists, which is the jsdom default.
      mql?.addEventListener?.('change', cb)
      return () => mql?.removeEventListener?.('change', cb)
    },
    [query],
  )
  const snapshot = useCallback(() => list(query)?.matches ?? false, [query])
  // The server snapshot is `false`: this console does not server-render, and a
  // hook that has to name a default should name the one that adds nothing to
  // the screen.
  return useSyncExternalStore(subscribe, snapshot, () => false)
}

/** True where the primary pointer is a finger — a phone or a tablet. */
export function useCoarsePointer(): boolean {
  return useMediaQuery(COARSE)
}

/**
 * True on a phone: a finger *and* a screen too narrow for two columns.
 *
 * Both halves are needed and neither is enough. A 1024px tablet is a finger and
 * has room for the cockpit's two columns, so sizing off the pointer alone would
 * fold a perfectly good layout away; a 700px browser window on a desktop is
 * narrow and has a keyboard and a mouse, so sizing off the width alone would
 * take the columns from somebody who can use them.
 */
export function usePhoneLayout(): boolean {
  const coarse = useMediaQuery(COARSE)
  const narrow = useMediaQuery(NARROW)
  return coarse && narrow
}
